import { t } from 'i18next';
import { set } from 'lodash-es';
import { Resolver } from 'react-hook-form';
import * as yup from 'yup';

import {
  getProviderBlockSchema,
  normalizeSttKeywords,
  repackElevenLabsPronunciationDictionary,
} from '@/components/pages/Integration/schemas';
import {
  resolveDeepgramLanguage,
  STT_PROVIDER_LANGUAGE_MAP,
  TTS_PROVIDER_LANGUAGE_MAP,
} from '@/constants/voiceProviderLanguageMap';
import { SpeechModelProvider, SpeechProvider } from '@/modules/developerTools/types';
import {
  DEFAULT_LANGUAGE,
  SpeechKind,
  SpeechStepConfig,
  SpeechStrategy,
  SpeechStrategyStep,
} from '@/shared/types/speechStrategy';
import { STTProvider, TTSModelInfo, TTSProvider } from '@/shared/types/voiceProviders';
import { createYupResolver } from '@/util/forms/createYupResolver';

/** One language row of the form. The UI edits the first step; the rest of the chain is kept. */
export interface SpeechStrategyEntry {
  language: string;
  step: SpeechStrategyStep | null;
  fallbacks: SpeechStrategyStep[];
}

export type SpeechStrategyForm = Record<SpeechKind, SpeechStrategyEntry[]>;

export const SPEECH_KINDS: SpeechKind[] = ['stt', 'tts'];

/** Where each provider block keeps its model id. */
export const MODEL_FIELD: Record<SpeechKind, Partial<Record<SpeechModelProvider, string>>> = {
  stt: { deepgram: 'model', elevenlabs: 'model_id', soniox: 'model' },
  tts: {
    elevenlabs: 'model_id',
    cartesia: 'model_id',
    minimax: 'model_id',
    deepgram: 'model_id',
    rime: 'model_id',
    google: 'model_id',
  },
};

/** Rime's languages per model, as orchestrator validates `language_code`. */
export const rimeLanguages = (model: string | undefined): string[] =>
  model === 'mistv3'
    ? ['en', 'fr', 'de', 'es']
    : ['ar', 'en', 'fr', 'de', 'hi', 'it', 'ja', 'nl', 'pt', 'es'];

/** `config.tts_cartesia.language_code` → `language_code`. */
const relativeLanguagePath = (fieldPath: string) => fieldPath.split('.').slice(2).join('.');

const languageField = (kind: SpeechKind, provider: SpeechModelProvider) =>
  kind === 'tts'
    ? TTS_PROVIDER_LANGUAGE_MAP[provider as TTSProvider]
    : STT_PROVIDER_LANGUAGE_MAP[provider as STTProvider];

/** Deepgram gates the language by model, e.g. flux-general-en is English-only. */
export const supportsLanguage = (params: {
  kind: SpeechKind;
  provider: SpeechModelProvider;
  model: string;
  language: string;
}): boolean => {
  const { kind, provider, model, language } = params;
  if (kind !== 'stt' || provider !== 'deepgram' || language === DEFAULT_LANGUAGE) {
    return true;
  }
  return (
    resolveDeepgramLanguage(language, model) ===
    STT_PROVIDER_LANGUAGE_MAP.deepgram.toProvider(language)
  );
};

/**
 * The voice catalog models that the speech models catalog accepts and that can speak the row's
 * language; a model without a list, or `multi`, speaks all.
 */
export const ttsModelsForLanguage = (
  voiceModels: TTSModelInfo[],
  provider: TTSProvider,
  language: string,
  catalog: SpeechProvider[]
): TTSModelInfo[] => {
  const accepted = catalog.find((entry) => entry.provider === provider)?.tts ?? [];
  const models = voiceModels.filter(({ id }) => accepted.includes(id));
  // MiniMax voices are multilingual and its language is only a hint, as on the integration page
  const languageMap = TTS_PROVIDER_LANGUAGE_MAP[provider];
  if (language === DEFAULT_LANGUAGE || provider === 'minimax' || !languageMap) {
    return models;
  }
  const code = languageMap.toProvider(language);
  return models.filter(
    ({ languages }) => !languages || languages.includes('multi') || languages.includes(String(code))
  );
};

/** The first model of the provider for the kind, and the provider's code for the row's language. */
export const defaultConfig = (params: {
  kind: SpeechKind;
  provider: SpeechModelProvider;
  language: string;
  catalog: SpeechProvider[];
}): SpeechStepConfig => {
  const { kind, provider, language, catalog } = params;
  // A voice field that mounts on an absent value takes the stored step's voice, so start it empty
  const config: SpeechStepConfig = kind === 'tts' ? { voice_id: '' } : {};
  const models = catalog.find((entry) => entry.provider === provider)?.[kind] ?? [];
  const model =
    models.find((id) => supportsLanguage({ kind, provider, model: id, language })) ?? models[0];
  const modelField = MODEL_FIELD[kind][provider];
  if (model && modelField) {
    set(config, modelField, model);
  }
  const field = languageField(kind, provider);
  if (language !== DEFAULT_LANGUAGE && field) {
    set(config, relativeLanguagePath(field.fieldPath), field.toProvider(language));
  }
  const base = language.split('-')[0];
  if (provider === 'rime' && rimeLanguages(model).includes(base)) {
    config.language_code = base;
  }
  return config;
};

const byLanguage = (a: SpeechStrategyEntry, b: SpeechStrategyEntry) => {
  if (a.language === DEFAULT_LANGUAGE) {
    return -1;
  }
  if (b.language === DEFAULT_LANGUAGE) {
    return 1;
  }
  return a.language.localeCompare(b.language);
};

const isElevenLabsTts = (kind: SpeechKind, step: SpeechStrategyStep) =>
  kind === 'tts' && step.provider === 'elevenlabs';

interface DictionaryLocator {
  pronunciation_dictionary_id: string;
  version_id?: string;
}

/** The ElevenLabs card edits one dictionary id and version; the provider stores a locator list. */
const toFormStep = (kind: SpeechKind, step?: SpeechStrategyStep): SpeechStrategyStep | null => {
  if (!step || !isElevenLabsTts(kind, step)) {
    return step ?? null;
  }
  const { pronunciation_dictionary_locators: locators, ...config } = step.config as {
    pronunciation_dictionary_locators?: DictionaryLocator[];
  };
  if (!locators?.[0]) {
    return step;
  }
  return {
    ...step,
    config: {
      ...config,
      pronunciation_dictionary_id: locators[0].pronunciation_dictionary_id,
      pronunciation_dictionary_version_id: locators[0].version_id,
    },
  };
};

/**
 * The primary row reads a legacy `*` chain when it has none of its own, so the next save moves
 * it under the primary language. A region default for the primary would win over `*`.
 */
export const adoptLegacyDefault = (strategy: SpeechStrategy, primary: string): SpeechStrategy => {
  const adopt = (chains: Record<string, SpeechStrategyStep[]> = {}) => {
    const { [DEFAULT_LANGUAGE]: legacy, ...rest } = chains;
    return legacy && !rest[primary] ? { ...rest, [primary]: legacy } : chains;
  };
  return { stt: adopt(strategy.stt), tts: adopt(strategy.tts) };
};

/** Every stored row, plus an empty one for `language` when it has none. */
export const toForm = (
  strategy: SpeechStrategy,
  language: string = DEFAULT_LANGUAGE
): SpeechStrategyForm => {
  const form = { stt: [], tts: [] } as SpeechStrategyForm;
  SPEECH_KINDS.forEach((kind) => {
    const entries = Object.entries(strategy[kind] ?? {}).map(([code, chain]) => ({
      language: code,
      step: toFormStep(kind, chain[0]),
      fallbacks: chain.slice(1),
    }));
    if (!entries.some((entry) => entry.language === language)) {
      entries.push({ language, step: null, fallbacks: [] });
    }
    form[kind] = entries.sort(byLanguage);
  });
  return form;
};

/** The settings inputs write `null` or `''` for a cleared field; the provider schemas want it absent. */
const withoutEmpty = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(withoutEmpty);
  }
  if (!value || typeof value !== 'object') {
    return value;
  }
  return Object.fromEntries(
    Object.entries(value)
      .filter(([, entry]) => entry !== null && entry !== undefined && entry !== '')
      .map(([key, entry]) => [key, withoutEmpty(entry)])
  );
};

/** The same clean-up a voice integration gets before it saves its provider blocks. */
const toStepConfig = (kind: SpeechKind, step: SpeechStrategyStep): SpeechStepConfig => {
  const config = withoutEmpty(step.config) as SpeechStepConfig;
  if (isElevenLabsTts(kind, step)) {
    repackElevenLabsPronunciationDictionary(config);
  }
  if (kind === 'stt') {
    normalizeSttKeywords({ [`stt_${step.provider}`]: config });
  }
  return config;
};

export const fromForm = (form: SpeechStrategyForm): SpeechStrategy => {
  const strategy: SpeechStrategy = { stt: {}, tts: {} };
  SPEECH_KINDS.forEach((kind) => {
    form[kind].forEach(({ language, step, fallbacks }) => {
      if (step) {
        const config = toStepConfig(kind, step);
        strategy[kind][language] = [{ ...step, config }, ...fallbacks];
      }
    });
  });
  return strategy;
};

// Orchestrator requires the voice of a TTS step; the voice integration blocks leave it optional
const configSchema = (kind: SpeechKind, provider: string) => {
  const block = getProviderBlockSchema(`${kind}_${provider.replace('-', '_')}`) ?? yup.object();
  return kind === 'tts'
    ? (block as yup.AnyObjectSchema).shape({
        voice_id: yup.string().required(() => t('validation.required')),
      })
    : block;
};

const stepSchema = (kind: SpeechKind) =>
  yup.lazy((step: SpeechStrategyStep | null) =>
    step ? yup.object({ config: configSchema(kind, step.provider) }) : yup.mixed().nullable()
  );

/** Each step's config gets the rules of the voice integration's block for its provider. */
export const speechStrategySchema = () =>
  yup.object({
    stt: yup.array().of(yup.object({ step: stepSchema('stt') })),
    tts: yup.array().of(yup.object({ step: stepSchema('tts') })),
  });

/** RHF gives an untouched card field `null` on a row whose default step is `null`. */
export const speechStrategyResolver = (): Resolver<SpeechStrategyForm> => {
  // `raw` keeps Yup's cast from adding empty provider blocks to the saved document
  const resolve = createYupResolver<SpeechStrategyForm>(speechStrategySchema(), { raw: true });
  return (values, context, options) =>
    resolve(withoutEmpty(values) as SpeechStrategyForm, context, options);
};
